import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { APP_NAME, APP_TAGLINE } from "@/config";
import { API_BASE } from "@/lib/api";
import { APP_VERSION } from "@/version";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, status } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Batch V1.2 #2 — brute-force lockout countdown.
  const [lockoutSec, setLockoutSec] = useState(0);

  useEffect(() => {
    if (status === "authed") navigate("/", { replace: true });
  }, [status, navigate]);

  // Countdown timer while locked out.
  useEffect(() => {
    if (lockoutSec <= 0) return;
    const t = setInterval(() => {
      setLockoutSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [lockoutSec]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || lockoutSec > 0) return;
    setSubmitting(true);
    const res = await login(username.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.full_name}`);
      navigate("/", { replace: true });
    } else if (res.lockout) {
      setLockoutSec(res.lockout.retry_after_seconds);
      toast.error(res.error || "Too many failed login attempts.");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full flex items-center justify-center relative bg-background text-foreground"
      style={{
        backgroundImage:
          "linear-gradient(to right, hsl(var(--border) / 0.4) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.4) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/40 to-background pointer-events-none" />

      <form
        onSubmit={onSubmit}
        data-testid="login-form"
        className="relative z-10 w-full max-w-md mx-6 bg-card border border-border rounded-md shadow-lg p-8 space-y-6"
      >
        <div className="text-center space-y-2">
          {company.logo_file_id ? (
            <img
              src={`${API_BASE}/settings/company/logo/public?v=${company.logo_file_id}`}
              alt={company.name}
              className="mx-auto h-12 max-w-[160px] object-contain"
              data-testid="login-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="mx-auto h-10 w-10 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
              <Lock className="h-5 w-5" />
            </div>
          )}
          <h1 className="text-2xl font-bold tracking-tight" data-testid="login-title">
            {company.name || APP_NAME}
          </h1>
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
            {APP_TAGLINE}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Username
            </Label>
            <Input
              id="username"
              data-testid="login-username-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || lockoutSec > 0}
          data-testid="login-submit-button"
          className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-sm bg-black text-white text-sm font-medium hover:bg-black/90 active:scale-[0.99] disabled:opacity-70 transition-all shadow-[0_0_20px_rgba(0,0,0,0.35)] dark:bg-white dark:text-black dark:shadow-[0_0_20px_rgba(255,255,255,0.25)]"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {lockoutSec > 0
            ? `Locked · ${Math.ceil(lockoutSec / 60)} min`
            : submitting ? "Signing in…" : "Sign in"}
        </button>
        {lockoutSec > 0 && (
          <div
            className="text-xs text-destructive text-center mt-2"
            data-testid="login-lockout-banner"
          >
            Too many failed attempts. Try again in {Math.ceil(lockoutSec / 60)} minute(s).
          </div>
        )}
        <div
          className="pt-4 text-center text-[10px] text-muted-foreground/70 tracking-wide"
          data-testid="app-version-display-login"
        >
          v{APP_VERSION}
        </div>
      </form>
    </div>
  );
}
